import {http, HttpResponse} from 'msw';
import {SetupServerApi} from 'msw/node';

import {VatRate, GetCurrenciesApiResponse} from '@dms/api/shared';

const getVatRatesResponseMock: VatRate[] = [
  {
    code: 'CZE',
    rates: [
      {
        type: 'S',
        name: 'Standard VAT rate',
        rate: '21',
        activeFrom: '2013-01-01',
      },
      {
        type: 'R',
        name: 'Reduced VAT rate',
        rate: '12',
        activeFrom: '2024-01-01',
      },
      {
        type: 'E',
        name: 'Exempt from VAT',
        rate: '0',
        activeFrom: '1993-01-01',
      },
      {
        type: 'Z',
        name: 'Zero VAT rate',
        rate: '0',
        activeFrom: '1993-01-01',
      },
    ],
    calculationRule: {
      formulaFromWithoutVat: 'STANDARD_FROM_BELOW',
      formulaFromWithVat: 'STANDARD_FROM_ABOVE',
      roundType: 'ALGEBRAIC',
      roundScale: 2,
      roundStep: 1,
      activeFrom: '1993-01-01',
    },
  },
  {
    code: 'SVK',
    rates: [
      {
        type: 'S',
        name: 'Standard VAT rate',
        rate: '20',
        activeFrom: '2011-01-01',
      },
      {
        type: 'R',
        name: 'Reduced VAT rate',
        rate: '10',
        activeFrom: '2007-01-01',
      },
      {
        type: 'E',
        name: 'Exempt from VAT',
        rate: '0',
        activeFrom: '2007-01-01',
      },
      {
        type: 'Z',
        name: 'Zero VAT rate',
        rate: '0',
        activeFrom: '2007-01-01',
      },
    ],
    calculationRule: {
      formulaFromWithoutVat: 'STANDARD_FROM_BELOW',
      formulaFromWithVat: 'SLOVAKIAN_FROM_ABOVE',
      roundType: 'ALGEBRAIC',
      roundScale: 2,
      roundStep: 1,
      activeFrom: '2007-01-01',
    },
  },
  {
    code: 'POL',
    rates: [
      {
        type: 'S',
        name: 'Standard VAT rate',
        rate: '23',
        activeFrom: '2011-01-01',
      },
      {
        type: 'MR',
        name: 'Mid-reduced VAT rate',
        rate: '8',
        activeFrom: '2011-01-01',
      },
      {
        type: 'R',
        name: 'Reduced VAT rate',
        rate: '5',
        activeFrom: '2011-01-01',
      },
      {
        type: 'Z',
        name: 'Zero VAT rate',
        rate: '0',
        activeFrom: '2000-01-01',
      },
      {
        type: 'E',
        name: 'Exempt from VAT',
        rate: '0',
        activeFrom: '2000-01-01',
      },
    ],
    calculationRule: {
      formulaFromWithoutVat: 'STANDARD_FROM_BELOW',
      formulaFromWithVat: 'STANDARD_FROM_ABOVE',
      roundType: 'ALGEBRAIC',
      roundScale: 2,
      roundStep: 1,
      activeFrom: '2000-01-01',
    },
  },
  {
    code: 'BEL',
    rates: [
      {
        type: 'S',
        name: 'Standard VAT rate',
        rate: '21',
        activeFrom: '2020-01-01',
      },
      {
        type: 'MR',
        name: 'Mid-reduced VAT rate',
        rate: '6',
        activeFrom: '2020-01-01',
      },
      {
        type: 'R',
        name: 'Reduced VAT rate',
        rate: '12',
        activeFrom: '2011-01-01',
      },
      {
        type: 'Z',
        name: 'Zero VAT rate',
        rate: '0',
        activeFrom: '2000-01-01',
      },
      {
        type: 'E',
        name: 'Exempt from VAT',
        rate: '0',
        activeFrom: '2000-01-01',
      },
    ],
    calculationRule: {
      formulaFromWithoutVat: 'STANDARD_FROM_BELOW',
      formulaFromWithVat: 'STANDARD_FROM_ABOVE',
      roundType: 'ALGEBRAIC',
      roundScale: 2,
      roundStep: 1,
      activeFrom: '2000-01-01',
    },
  },
  {
    code: 'DEU',
    rates: [
      {
        type: 'S',
        name: 'Standard VAT rate',
        rate: '19',
        activeFrom: '2024-01-01',
      },
      {
        type: 'R',
        name: 'Reduced VAT rate',
        rate: '7',
        activeFrom: '2024-01-01',
      },
      {
        type: 'Z',
        name: 'Zero VAT rate',
        rate: '0',
        activeFrom: '2000-01-01',
      },
      {
        type: 'E',
        name: 'Exempt from VAT',
        rate: '0',
        activeFrom: '2000-01-01',
      },
    ],
    calculationRule: {
      formulaFromWithoutVat: 'STANDARD_FROM_BELOW',
      formulaFromWithVat: 'STANDARD_FROM_ABOVE',
      roundType: 'ALGEBRAIC',
      roundScale: 2,
      roundStep: 1,
      activeFrom: '2000-01-01',
    },
  },
];

const getVatRatesResponseInterceptor = (server: SetupServerApi) =>
  server.use(http.get('*/common/v1/vat-rates', () => HttpResponse.json(getVatRatesResponseMock)));

const getCurrenciesResponseMock: GetCurrenciesApiResponse = [
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    preferred: false,
  },
  {
    code: 'AMD',
    name: 'Armenian Dram',
    symbol: '֏',
    preferred: false,
  },
  {
    code: 'AZN',
    name: 'Azerbaijanian Manat',
    symbol: '₼',
    preferred: false,
  },
  {
    code: 'BAM',
    name: 'Convertible Mark',
    symbol: 'KM',
    preferred: false,
  },
  {
    code: 'BGN',
    name: 'Bulgarian Lev',
    symbol: 'лв.',
    preferred: false,
  },
  {
    code: 'CZK',
    name: 'Czech crown',
    symbol: 'Kč',
    preferred: false,
  },
  {
    code: 'DKK',
    name: 'Danish Krone',
    symbol: 'kr',
    preferred: false,
  },
  {
    code: 'GEL',
    name: 'Lari',
    symbol: '₾',
    preferred: false,
  },
  {
    code: 'GBP',
    name: 'Pound Sterling',
    symbol: '£',
    preferred: false,
  },
  {
    code: 'ISK',
    name: 'Iceland Krona',
    symbol: 'kr',
    preferred: false,
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    symbol: 'Fr.',
    preferred: false,
  },
  {
    code: 'MKD',
    name: 'Denar',
    symbol: 'ден',
    preferred: false,
  },
  {
    code: 'MDL',
    name: 'Moldovan Leu',
    symbol: 'L',
    preferred: false,
  },
  {
    code: 'NOK',
    name: 'Norwegian Krone',
    symbol: 'kr',
    preferred: false,
  },
  {
    code: 'PLN',
    name: 'Zloty',
    symbol: 'zł',
    preferred: false,
  },
  {
    code: 'RON',
    name: 'New Romanian Leu',
    symbol: 'Lei',
    preferred: false,
  },
  {
    code: 'RUB',
    name: 'Russian Ruble',
    symbol: '₽',
    preferred: false,
  },
  {
    code: 'RSD',
    name: 'Serbian Dinar',
    symbol: 'din.',
    preferred: false,
  },
  {
    code: 'SEK',
    name: 'Swedish Krona',
    symbol: 'kr',
    preferred: false,
  },
  {
    code: 'TRY',
    name: 'Turkish Lira',
    symbol: '₺',
    preferred: false,
  },
  {
    code: 'UAH',
    name: 'Hryvnia',
    symbol: '₴',
    preferred: false,
  },
  {
    code: 'USD',
    name: 'United States dollar',
    symbol: '$',
    preferred: false,
  },
];

const getCurrenciesResponseInterceptor = (server: SetupServerApi) =>
  server.use(
    http.get('*/common/v1/currencies', () => HttpResponse.json(getCurrenciesResponseMock))
  );

export const cachedApiMock = {
  getVatRatesResponseMock,
  getVatRatesResponseInterceptor,
  getCurrenciesResponseMock,
  getCurrenciesResponseInterceptor,
};
